/**
 * Sessions list page for the plugin builder.
 * Shows all plugin builder sessions with create/delete actions.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePluginBuilderSessions } from '../hooks/usePluginBuilderSessions';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Loader2, Plus, Trash2, Clock, Code2, LayoutDashboard } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function PluginSessionsListPage() {
  const navigate = useNavigate();
  const { sessions, isLoading, isCreating, error, createSession, deleteSession } =
    usePluginBuilderSessions();
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleNewSession = async () => {
    await createSession();
  };

  const handleDeleteSession = async (sessionId: string) => {
    await deleteSession(sessionId);
  };

  const formatCost = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const statusColors = {
    active: 'bg-green-500/10 text-green-600 border-green-500/20',
    paused: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    completed: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    abandoned: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold">Plugin Builder</h1>
          <FavoriteButton archeId="arche_plugin_builder" />
        </div>
        <p className="text-muted-foreground mt-1">
          Create custom Arches through conversations with Fias AI
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={handleNewSession} disabled={isCreating}>
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            New Plugin
          </Button>
          <Button variant="outline" onClick={() => navigate('/contributor/dashboard')}>
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error.message}</p>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && sessions.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <Code2 className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No plugins yet</h3>
            <p className="text-muted-foreground mb-4">
              Start a new session to begin building your plugin with React and TypeScript
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={handleNewSession} disabled={isCreating}>
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                New Plugin
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sessions grid */}
      {!isLoading && sessions.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sessions.map((session) => (
            <Card
              key={session.sessionId}
              className="group hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/a/arche_plugin_builder/${session.sessionId}`)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">
                      {session.currentName || 'Untitled Plugin'}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {session.currentDescription || 'No description'}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className={statusColors[session.status]}>
                    {session.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    <span>
                      {formatDistanceToNow(new Date(session.updatedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <span>{formatCost(session.totalCostCents)} credits</span>
                </div>

                {/* Delete button */}
                <div className="mt-3 flex justify-end">
                  <AlertDialog onOpenChange={(open) => !open && setDeleteConfirmText('')}>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Session</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this session? This action cannot be
                          undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-2">
                          Type <span className="font-mono font-semibold">delete</span> to confirm:
                        </p>
                        <Input
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="delete"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSession(session.sessionId);
                            setDeleteConfirmText('');
                          }}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
